import type { CSSProperties } from "react";
import type { DashboardData, Day, Event } from "../types";
import { StatusBadge } from "../components/StatusBadge";
import { IconActionButton } from "../components/AdminActions";

type Props = {
  event: Event;
  data: DashboardData | null;
  days: Day[];
  loading: boolean;
  onNewSession: () => void;
  onNavigate: (page: "schedule" | "speakers" | "abstracts" | "reports") => void;
};

const dateLong = new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

export function DashboardPage({ event, data, days, loading, onNewSession, onNavigate }: Props) {
  const callForScienceEnabled = Boolean(event.abstractsEnabled);
  const metrics = data?.metrics;
  const confirmedRate = metrics?.speakers ? Math.round((metrics.confirmed_speakers / metrics.speakers) * 100) : 0;

  const programRate = metrics?.program_sessions ? Math.round(metrics.confirmed_sessions / metrics.program_sessions * 100) : 0;
  const evaluationRate = metrics?.reviewable_abstracts ? Math.round(metrics.evaluated_abstracts / metrics.reviewable_abstracts * 100) : 0;
  const startDate = new Date(event.startDate);

  return (
    <div className="page-stack">
      <section className="event-hero">
        <div className="event-hero-copy">
          <span className="hero-kicker">Evento activo · {event.city}</span>
          <h1>{event.name}</h1>
          <p>{dateLong.format(new Date(event.startDate))} — {dateLong.format(new Date(event.endDate))} · {event.venue}</p>
          <div className="hero-actions">
            <IconActionButton label="Nueva sesión" icon="add" tone="primary" onClick={onNewSession} />
            <IconActionButton label="Ver agenda completa" icon="calendar" onClick={() => onNavigate("schedule")} />
          </div>
        </div>
        <div className="hero-date-card" aria-label="Fecha de inicio">
          <span>{new Intl.DateTimeFormat("es-AR", { month: "short", timeZone: "UTC" }).format(startDate).toUpperCase()}</span>
          <strong>{startDate.getUTCDate()}</strong>
          <small>{days.length} {days.length === 1 ? "jornada" : "jornadas"}</small>
        </div>
        <div className="hero-orbit orbit-one" />
        <div className="hero-orbit orbit-two" />
      </section>

      <section className={`metric-grid${callForScienceEnabled ? "" : " metric-grid-without-abstracts"}`} aria-label="Resumen del evento">
        <MetricCard label="Faculty" value={metrics?.speakers} detail={`${metrics?.confirmed_speakers || 0} confirmados`} tone="teal" loading={loading} onClick={() => onNavigate("speakers")} />
        <MetricCard label="Sesiones" value={metrics?.sessions} detail={`${days.length} jornadas`} tone="navy" loading={loading} onClick={() => onNavigate("schedule")} />
        {callForScienceEnabled && <MetricCard label="Trabajos" value={metrics?.abstracts} detail={`${metrics?.pending_abstracts || 0} por evaluar`} tone="amber" loading={loading} onClick={() => onNavigate("abstracts")} />}
        <MetricCard label="Salas activas" value={metrics?.rooms} detail="Agenda sincronizada" tone="violet" loading={loading} onClick={() => onNavigate("schedule")} />
      </section>

      <div className="dashboard-grid">
        <section className="panel agenda-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Próximas actividades</span>
              <h2>Agenda en foco</h2>
            </div>
            <IconActionButton label="Ver agenda" icon="calendar" onClick={() => onNavigate("schedule")} />
          </div>
          <div className="day-strip">
            {days.map((day, index) => <span className={index === 0 ? "active" : ""} key={day.id}>{day.name}</span>)}
          </div>
          <div className="agenda-list">
            {loading ? Array.from({ length: 4 }).map((_, index) => <div className="skeleton agenda-skeleton" key={index} />) : data?.nextSessions.slice(0, 5).map((session) => (
              <article className="agenda-item" key={session.id}>
                <div className="agenda-time"><strong>{session.startTime}</strong><span>{session.endTime}</span></div>
                <span className="topic-line" style={{ background: session.topicColor || "#0b918d" }} />
                <div className="agenda-copy">
                  <div className="agenda-meta"><span>{session.roomName}</span><span>·</span><span>{session.topicName || "Programa general"}</span></div>
                  <h3>{session.title}</h3>
                  <p>{session.speakerCount} participantes asignados</p>
                </div>
                <StatusBadge status={session.status} />
              </article>
            ))}
          </div>
        </section>

        <aside className="panel readiness-panel">
          <div className="panel-heading compact">
            <div>
              <span className="eyebrow">Preparación</span>
              <h2>Estado del evento</h2>
            </div>
          </div>
          <div className="progress-ring" style={{ "--progress": `${confirmedRate * 3.6}deg` } as CSSProperties}>
            <div><strong>{confirmedRate}%</strong><span>faculty confirmado</span></div>
          </div>
          <div className="readiness-list">
            <ReadinessRow label="Sesiones confirmadas" value={programRate} />
            <ReadinessRow label="Confirmaciones" value={confirmedRate} />
            {callForScienceEnabled && <ReadinessRow label="Trabajos con dictamen final" value={evaluationRate} />}
          </div>
          <IconActionButton label="Ver reporte general" icon="report" onClick={() => onNavigate("reports")} />
        </aside>
      </div>

      {callForScienceEnabled && <section className="panel abstracts-preview">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Call for Science</span>
            <h2>Trabajos recientes</h2>
          </div>
          <IconActionButton label="Gestionar trabajos" icon="report" onClick={() => onNavigate("abstracts")} />
        </div>
        <div className="responsive-table">
          <table>
            <thead><tr><th>ID</th><th>Título</th><th>Responsable</th><th>País</th><th>Puntaje</th><th>Estado</th></tr></thead>
            <tbody>
              {data?.recentAbstracts.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.code}</strong></td>
                  <td className="title-cell">{item.title}</td>
                  <td>{item.submitterName}</td>
                  <td>{item.country}</td>
                  <td>{item.score == null ? "—" : item.score.toFixed(1)}</td>
                  <td><StatusBadge status={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>}
    </div>
  );
}

function MetricCard({ label, value, detail, tone, loading, onClick }: { label: string; value?: number; detail: string; tone: string; loading: boolean; onClick: () => void }) {
  return (
    <button className={`metric-card metric-${tone}`} onClick={onClick}>
      <span className="metric-label">{label}</span>
      {loading ? <span className="skeleton metric-value-skeleton" /> : <strong>{value ?? 0}</strong>}
      <span className="metric-detail">{detail}</span>
      <span className="metric-arrow">↗</span>
    </button>
  );
}

function ReadinessRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="readiness-row">
      <div><span>{label}</span><strong>{value}%</strong></div>
      <div className="mini-progress"><span style={{ width: `${value}%` }} /></div>
    </div>
  );
}
